import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, X, Copy } from "lucide-react";

export default function HolisticSummaryModal({ studentId, onClose }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get(`/support-docs/${studentId}/summary`)
      .then((r) => { if (r.data?.content?.text) setText(r.data.content.text); })
      .finally(() => setLoading(false));
  }, [studentId]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/students/${studentId}/holistic-summary`);
      setText(data.text);
      toast.success("Holistic summary ready");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const copy = () => { navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl p-6 animate-rise" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-['Outfit'] text-xl font-bold text-slate-800 flex items-center gap-2">
            <Sparkles size={20} className="text-[#5d8a73]" /> Holistic Assessment Summary
          </h3>
          <button data-testid="summary-close-btn" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <p className="text-sm text-slate-500 mb-4">A rounded summary drawing on curriculum levels, standardised tests, running assessments and observations.</p>

        {loading ? (
          <div className="flex justify-center py-10 text-slate-300"><Loader2 className="animate-spin" /></div>
        ) : (
          <>
            {text ? (
              <textarea
                data-testid="summary-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={12}
                className="w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 leading-relaxed outline-none focus:border-[#81B29A] focus:bg-white resize-none whitespace-pre-wrap"
              />
            ) : (
              <div className="text-center py-8 text-slate-400 text-sm border border-dashed border-slate-200 rounded-xl">
                No summary yet. Generate one from all of this student's data.
              </div>
            )}
            <div className="flex items-center gap-2 mt-4">
              <button data-testid="generate-summary-btn" onClick={generate} disabled={generating}
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold font-['Outfit'] text-white transition disabled:opacity-60"
                style={{ background: "linear-gradient(135deg,#81B29A,#5d8a73)" }}>
                {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                {generating ? "Generating…" : text ? "Regenerate" : "Generate summary"}
              </button>
              {text && (
                <button data-testid="copy-summary-btn" onClick={copy}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold font-['Outfit'] bg-white border border-slate-200 text-slate-700 hover:border-[#81B29A] transition">
                  <Copy size={15} /> Copy
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
